import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IClientBackPassportData,
  IClientFrontPassportData,
  IOperator,
  IOrganization,
  IOtpCode,
  IUserDataByTin,
} from "../types/UserTypes";
import { verification } from "../requests/IdentificationRequests";

interface IUserState {
  frontPassportData: IClientFrontPassportData | undefined;
  backPassportData: IClientBackPassportData | undefined;
  compareFacesResult: boolean;
  checkLivenessResult: boolean;
  operator: IOperator | undefined;
  clientPhoneNumber: string | undefined;
  otp: IOtpCode;
  client: IUserDataByTin | undefined;
  clientOrganization: IOrganization | undefined;
  isLoading: boolean;
  isCameraLoading: boolean;
  error: string | null;
}

const initialState: IUserState = {
  frontPassportData: undefined,
  backPassportData: undefined,
  compareFacesResult: false,
  checkLivenessResult: false,
  operator: undefined,
  clientOrganization: undefined,
  otp: {
    wasTheCodeSent: false,
    expireAt: "",
  },
  clientPhoneNumber: undefined,
  client: undefined,
  isLoading: false,
  isCameraLoading: false,
  error: null,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    resetStates(state) {
      state.frontPassportData = undefined;
      state.backPassportData = undefined;
      state.compareFacesResult = false;
      state.checkLivenessResult = false;
      state.client = undefined;
      state.clientPhoneNumber = undefined;
      state.clientOrganization = undefined;
    },

    resetError(state) {
      state.error = null;
    },

    sendCodeLoading(state) {
      state.isLoading = true;
      state.error = "";
      state.otp.wasTheCodeSent = false;
      state.otp.expireAt = "";
    },

    sendCodeSuccess(state, action: PayloadAction<IOtpCode>) {
      state.isLoading = false;
      state.error = "";
      state.otp = action.payload;
    },

    sendCodeFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    verifyCodeLoading(state) {
      state.isLoading = true;
      state.error = "";
    },

    verifyCodeSuccess(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = "";
      state.clientPhoneNumber = action.payload;
      state.otp.wasTheCodeSent = false;
      state.otp.expireAt = "";
    },

    verifyCodeFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
      state.clientPhoneNumber = "";
    },

    setOrganization(state, action: PayloadAction<IOrganization>) {
      state.clientOrganization = action.payload;
    },

    getFrontPassportDataLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    getFrontPassportDataSuccess(
      state,
      action: PayloadAction<IClientFrontPassportData>,
    ) {
      state.isLoading = false;
      state.error = null;
      state.frontPassportData = action.payload;
    },
    getFrontPassportDataFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getBackPassportDataLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    getBackPassportDataSuccess(
      state,
      action: PayloadAction<IClientBackPassportData>,
    ) {
      state.isLoading = false;
      state.error = null;
      state.backPassportData = action.payload;
    },
    getBackPassportDataFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getOperatorLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    getOperatorSuccess(state, action: PayloadAction<IOperator>) {
      state.operator = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getOperatorFailed(state) {
      state.isLoading = false;
    },
    getClientDataLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    getClientDataSuccess(state, action: PayloadAction<IUserDataByTin>) {
      state.client = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getClientDataFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    resetClientData(state) {
      state.client = undefined;
    },

    checkLivenessLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    checkLivenessSuccess(state) {
      state.isLoading = false;
      state.error = null;
      state.checkLivenessResult = true;
    },
    checkLivenessFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.checkLivenessResult = false;
      state.error = action.payload;
    },

    compareFacesLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    compareFacesSuccess(state) {
      state.isLoading = false;
      state.error = null;
      state.compareFacesResult = true;
    },
    compareFacesFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.compareFacesResult = false;
      state.error = action.payload;
    },
    setResultLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    setResultSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    setResultFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    updateImzoUserLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    updateImzoUserSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    updateImzoUserFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    loginLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    loginFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    openCameraLoading(state) {
      state.isCameraLoading = true;
      state.error = null;
    },
    openCameraSuccess(state) {
      state.isCameraLoading = false;
      state.error = null;
    },
    openCameraFailed(state, action: PayloadAction<string>) {
      state.isCameraLoading = false;
      state.error = action.payload;
    },
  },
});

export default userSlice.reducer;
